import { useForm } from "@tanstack/react-form";
import { useMemo } from "react";
import { Search } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "@/core/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/core/components/ui/card";
import { EntitySearchableSelect } from "@/core/components/ui/entity-searchable-select";
import { Label } from "@/core/components/ui/label";
import { useNotificationsTranslation } from "@/features/notification/i18n/use-notifications-translation";
import {
  createPartyLookupSchema,
  type PartyLookupFormValues,
} from "@/features/notification/schemas/notification-preference.schema";
import { usePartySelectOptions } from "@/features/shared/api";

/**
 * Recherche d'une identité pour charger ses préférences. Le partyId courant
 * est piloté par la page (query param `?partyId=...`) ; ce card met à jour
 * la valeur via le callback fourni.
 */
export function PartyLookupCard({
  partyId,
  onSelect,
}: {
  partyId?: string;
  onSelect: (partyId: string) => void;
}) {
  const { t } = useNotificationsTranslation();
  const { i18n } = useTranslation();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- i18n.language : dépendance voulue, schéma zod traduit reconstruit à chaque changement de langue.
  const schema = useMemo(() => createPartyLookupSchema(), [i18n.language]);
  const { options: partyOptions, isLoading: partiesLoading } =
    usePartySelectOptions(true);

  const form = useForm({
    defaultValues: { partyId: partyId ?? "" } satisfies PartyLookupFormValues,
    onSubmit: async ({ value }) => {
      const parsed = schema.safeParse(value);
      if (!parsed.success) return;
      onSelect(parsed.data.partyId);
    },
  });

  const fieldError =
    (name: keyof PartyLookupFormValues) => (props: { value: unknown }) => {
      const shape = schema.shape[name];
      const result = shape.safeParse(props.value ?? "");
      return result.success ? undefined : result.error.issues[0]?.message;
    };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">
          {t("notifications.preferences.lookup.title")}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form
          className="flex flex-col gap-3 sm:flex-row sm:items-end"
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            void form.handleSubmit();
          }}
        >
          <form.Field name="partyId" validators={{ onChange: fieldError("partyId") }}>
            {(field) => (
              <div className="flex-1 space-y-1.5">
                <Label htmlFor="preferences-party-id">
                  {t("notifications.preferences.lookup.partyId")}
                </Label>
                <EntitySearchableSelect
                  id="preferences-party-id"
                  options={partyOptions}
                  value={field.state.value}
                  onValueChange={(value) => field.handleChange(value)}
                  placeholder={t(
                    "notifications.preferences.lookup.partyIdPlaceholder"
                  )}
                  isLoading={partiesLoading}
                />
                {field.state.meta.errors.length > 0 && (
                  <p className="text-sm text-destructive">
                    {field.state.meta.errors[0]}
                  </p>
                )}
              </div>
            )}
          </form.Field>

          <Button type="submit" className="sm:w-auto">
            <Search className="mr-2 h-4 w-4" />
            {t("notifications.preferences.lookup.submit")}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
